import type { SmartFiltersResponse } from "../types";
import type { FreelancerFilterFormValues } from "./filterParams";
import { keepValidSubcategories } from "./subcategory-filter";

/** A search carries at most three ambiti. */
const MAX_SUBCATEGORIES = 3;

export type MapSmartFiltersOptions = {
  /** When false, price_per_day_* fields are dropped (e.g. fixed-offer context). */
  allowPriceFields?: boolean;
};

/** Map the AI smart-filters response to form-compatible values. */
export function mapSmartFiltersToFormValues(
  response: SmartFiltersResponse,
  options?: MapSmartFiltersOptions,
): Partial<FreelancerFilterFormValues> {
  const allowPriceFields = options?.allowPriceFields ?? true;
  const values: Partial<FreelancerFilterFormValues> = {};

  if (response.job_category) {
    values.job_category = response.job_category;
    // Always set with a category: a second extraction must not keep the
    // subcategories of the first one.
    values.job_subcategory = keepValidSubcategories(
      response.job_category,
      response.job_subcategory ?? [],
    ).slice(0, MAX_SUBCATEGORIES);
  }
  if (response.skills?.length) {
    values.skills = response.skills;
  }
  if (response.work_preference) {
    values.work_preference =
      response.work_preference === "flexible"
        ? "hybrid"
        : response.work_preference;
  }
  if (allowPriceFields && response.price_per_day_min) {
    values.price_per_day_min = response.price_per_day_min;
  }
  if (allowPriceFields && response.price_per_day_max) {
    values.price_per_day_max = response.price_per_day_max;
  }
  if (response.experience_level) {
    values.experience_level = response.experience_level;
  }
  if (response.available_from) {
    values.available_from = response.available_from;
  }

  return values;
}

/** Check whether the AI returned an empty / no-match response. */
export function isEmptySmartFiltersResponse(
  response: SmartFiltersResponse,
): boolean {
  return (
    !response.job_category &&
    (!response.skills || response.skills.length === 0) &&
    !response.work_preference &&
    !response.price_per_day_min &&
    !response.price_per_day_max &&
    !response.experience_level &&
    !response.available_from
  );
}
